import { Counter } from "@/components/ui/baseUi/counter"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { useFinanceStats } from "@/modules/finances/hooks/useFinance"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { PaymentPlace, TransactionFilterStatus } from "@/modules/finances/types/financesType"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { useQueryParam } from "@modules/utils/navigation/useQueryParam"
import { SearchIcon, XIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

type Props = {
  className?: string
} & React.ComponentProps<"div">

export function TransactionTableFilter({ className, ...props }: Props) {
  const [nameFilter, setNameFilter] = useState("")

  const [period] = usePeriodParam()
  const [_nameFilterParam, setNameFilterParam] = useQueryParam("nameFilter")
  const [status, setStatus] = useQueryParam("status")
  const [paymentPlace, setPaymentPlace] = useQueryParam("paymentPlace")
  const { data: stats } = useFinanceStats(true, period)

  useEffect(() => {
    setNameFilterParam(nameFilter)
  }, [nameFilter])

  const statusOptions = [
    { label: word("finances.page.filter.all"), value: "ALL" },
    { label: word("finances.page.table.status.succeded", { count: 1 }), value: TransactionFilterStatus.SUCCEEDED },
    { label: word("finances.page.table.status.pending", { count: 1 }), value: TransactionFilterStatus.PENDING },
    {
      label: word("finances.page.table.status.pendingSepa", { count: 1 }),
      value: TransactionFilterStatus.PENDING_SEPA_DEBIT,
    },
    { label: word("finances.page.table.status.disputed", { count: 1 }), value: TransactionFilterStatus.DISPUTED },
    { label: word("finances.page.table.status.failed", { count: 1 }), value: TransactionFilterStatus.FAILED },
    { label: word("finances.page.table.status.refunded", { count: 1 }), value: TransactionFilterStatus.REFUNDED },
  ]

  const paymentPlaceOptions = [
    { label: word("finances.page.table.paymentPlace.all"), value: "ALL" },
    { label: word("finances.page.table.paymentPlace.online"), value: PaymentPlace.ONLINE },
    { label: word("finances.page.table.paymentPlace.atCounter"), value: PaymentPlace.AT_COUNTER },
  ]

  const getTransactionTotalByStatus = useCallback(
    (filter: string): React.JSX.Element | null => {
      if (!stats?.counts) {
        return null
      }
      switch (filter) {
        case "ALL":
          return (
            <Counter variant="neutralSubtle" size="sm">
              {stats.counts.totalCount}
            </Counter>
          )
        case TransactionFilterStatus.SUCCEEDED:
          return (
            <Counter variant={stats.counts.succeededCount > 0 ? "positiveIntense" : "neutralSubtle"} size="sm">
              {stats.counts.succeededCount}
            </Counter>
          )
        case TransactionFilterStatus.PENDING:
          return (
            <Counter variant={stats.counts.pendingCount > 0 ? "positiveIntense" : "neutralSubtle"} size="sm">
              {stats.counts.pendingCount}
            </Counter>
          )
        case TransactionFilterStatus.PENDING_SEPA_DEBIT:
          return (
            <Counter variant={stats.counts.pendingSepaDebitCount > 0 ? "positiveIntense" : "neutralSubtle"} size="sm">
              {stats.counts.pendingSepaDebitCount}
            </Counter>
          )
        case TransactionFilterStatus.FAILED:
          return (
            <Counter variant={stats.counts.failedCount > 0 ? "negativeIntense" : "neutralSubtle"} size="sm">
              {stats.counts.failedCount}
            </Counter>
          )
        case TransactionFilterStatus.DISPUTED:
          return (
            <Counter variant={stats.counts.disputedCount > 0 ? "noticeIntense" : "neutralSubtle"} size="sm">
              {stats.counts.disputedCount}
            </Counter>
          )
        case TransactionFilterStatus.REFUNDED:
          return (
            <Counter variant={stats.counts.refundedCount > 0 ? "neutralIntense" : "neutralSubtle"} size="sm">
              {stats.counts.refundedCount}
            </Counter>
          )
        default:
          return null
      }
    },
    [stats?.counts],
  )

  const getTransactionTotalByPaymentPlace = useCallback(
    (paymentPlace: string): React.JSX.Element | null => {
      if (!stats?.counts) {
        return null
      }
      switch (paymentPlace) {
        case "ALL":
          return (
            <Counter variant={stats.counts.totalCount > 0 ? "neutralIntense" : "neutralSubtle"} size="sm">
              {stats.counts.totalCount}
            </Counter>
          )
        case PaymentPlace.ONLINE:
          return (
            <Counter variant={stats.counts.onlineCount > 0 ? "neutralIntense" : "neutralSubtle"} size="sm">
              {stats.counts.onlineCount}
            </Counter>
          )
        case PaymentPlace.AT_COUNTER:
          return (
            <Counter variant={stats.counts.atCounterCount > 0 ? "neutralIntense" : "neutralSubtle"} size="sm">
              {stats.counts.atCounterCount}
            </Counter>
          )
        default:
          return null
      }
    },
    [stats?.counts],
  )

  return (
    <div className={cn("flex flex-row gap-2", className)} {...props}>
      <InputGroup className="min-w-60">
        <InputGroupInput
          placeholder={word("global.search.placeholder")}
          onChange={(e) => {
            setNameFilter(e.currentTarget.value)
          }}
          value={nameFilter}
        />
        <InputGroupAddon align="inline-start">
          <SearchIcon color="currentColor" />
        </InputGroupAddon>

        {nameFilter && (
          <InputGroupAddon
            align="inline-end"
            onClick={() => {
              setNameFilter("")
            }}
          >
            <XIcon color="currentColor" className="cursor-pointer" />
          </InputGroupAddon>
        )}
      </InputGroup>

      <DropdownInput options={statusOptions} isRequiredInput={true}>
        <Select
          items={statusOptions}
          defaultValue="ALL"
          value={status ?? undefined}
          onValueChange={(value) => {
            setStatus(value ?? "")
          }}
        >
          <SelectTrigger id={"status"} className="min-w-60 bg-surface-background">
            <SelectValue placeholder={word("finances.page.table.status.placeholder")}>
              {(value) => {
                const option = statusOptions.find((o) => o.value === value)
                if (!option) return null
                return (
                  <div className="flex flex-row items-center gap-2">
                    {option.label}
                    {getTransactionTotalByStatus(option.value)}
                  </div>
                )
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>
                <Text fontStyle="LS" className="text-textOnSurface-muted">
                  {word("finances.page.table.status.placeholder")}
                </Text>
              </SelectLabel>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-row items-center gap-2">
                    {option.label}
                    {getTransactionTotalByStatus(option.value)}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </DropdownInput>

      <DropdownInput options={paymentPlaceOptions} isRequiredInput={true}>
        <Select
          items={paymentPlaceOptions}
          defaultValue="ALL"
          value={paymentPlace ?? undefined}
          onValueChange={(value) => {
            setPaymentPlace(value ?? "")
          }}
        >
          <SelectTrigger id={"paymentPlace"} className="min-w-60 bg-surface-background">
            <SelectValue placeholder={word("finances.page.table.paymentPlace.placeholder")}>
              {(value) => {
                const option = paymentPlaceOptions.find((o) => o.value === value)
                if (!option) return null
                return (
                  <div className="flex flex-row items-center gap-2">
                    {option.label}
                    {getTransactionTotalByPaymentPlace(option.value)}
                  </div>
                )
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>
                <Text fontStyle="LS" className="text-textOnSurface-muted">
                  {word("finances.page.table.paymentPlace.placeholder")}
                </Text>
              </SelectLabel>
              {paymentPlaceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-row items-center gap-2">
                    {option.label}
                    {getTransactionTotalByPaymentPlace(option.value)}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </DropdownInput>
    </div>
  )
}
